import { useEffect, useState } from "react";
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
} from "@mui/material";
import {
  ClipboardMinus,
  DoorClosed,
  File,
  Trash2,
  CircleUser as User,
  X,
} from "lucide-react";
import {
  NavLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { QueryContact } from "@/api/queries/types";
import { Button } from "@/components/Button/Button";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/context-menu";
import { ReportProduction } from "@/components/report-production";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/sidebar";
import {
  ContactStore,
  useLocalStorageStore,
} from "@/hooks/useLocalStorageStore";
import {
  deleteSearchHistory,
  getSearchHistory,
  removeAllExcept,
  removeSearchHistoryItem,
  SearchItem,
} from "@/layout/mainlayout";
import { cn } from "@/lib/utils";
import { useDocProducerUI } from "@/providers/DocProducerProvider";

export function NavContacts() {
  const [contacts, setContacts] = useState<SearchItem[]>(getSearchHistory());
  const [hoveredContact, setHoveredContact] = useState<ContactStore | null>(null);
  const [focusedContact, setFocusedContact] = useState<ContactStore | null>(null);
  const { contactId } = useParams<{ contactId: string }>();

  useEffect(() => {
    refreshContacts();
  }, [contactId]);

  const refreshContacts = () => {
    const openContacts = getSearchHistory();
    setContacts(openContacts);
    setHoveredContact(null);
    setFocusedContact(null);
  };

  const closeItem = (item: ContactStore) => {
    removeSearchHistoryItem(item.id);
    refreshContacts();
  };
  const closeAllOthers = (item: ContactStore) => {
    removeAllExcept(item.id);
    refreshContacts();
  };
  const closeAll = () => {
    deleteSearchHistory();
    refreshContacts();
  };

  if (!contacts || contacts.length === 0) {
    return <></>;
  }

  return (
    <>
      <SidebarGroup className="group-data-[collapsible=icon]:hidden pt-0">
        <SidebarGroupLabel className="h-10">
          <span className="line-clamp-2 break-words">
            {hoveredContact?.name ?? focusedContact?.name ?? "Contacts"}
          </span>
        </SidebarGroupLabel>
        <SidebarMenu className="grid grid-cols-2 gap-1">
          {contacts.map((item) => {
            return (
              <ContactItem
                onDelete={(i) => closeItem(item)}
                onDeleteAllOthers={(i) => closeAllOthers(item)}
                onDeleteAll={closeAll}
                onHover={setHoveredContact}
                onFocus={setFocusedContact}
                key={item.id}
                item={item}
              />
            );
          })}
        </SidebarMenu>
      </SidebarGroup>
    </>
  );
}

interface ContactItemProps {
  item: ContactStore;
  onDelete: (contact: ContactStore) => void;
  onDeleteAllOthers: (contact: ContactStore) => void;
  onDeleteAll: () => void;
  onHover: (contact: ContactStore | null) => void;
  onFocus: (contact: ContactStore | null) => void;
}
const ContactItem = ({
  item,
  onDelete,
  onDeleteAllOthers,
  onDeleteAll,
  onHover,
  onFocus,
}: ContactItemProps) => {
  // const [openContacts, setOpenContacts] = useLocalStorageStore();
  const navigate = useNavigate();
  // const handleDelete = (contact: ContactStore) => {
  //   const newContacts = openContacts.filter((c) => c.id !== contact.id);
  //   setOpenContacts(newContacts);
  //   if (parseInt(contactId || "0", 10) !== contact.id) return;
  // };

  const [reportOpen, setReportOpen] = useState(false);

  const { openDialog } = useDocProducerUI();

  return (
    <>
      <MuiDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <Button
          variant="icon"
          size="sm"
          className="absolute top-2 right-2"
          onClick={() => setReportOpen(false)}
        >
          <X className="size-4" />
        </Button>
        <MuiDialogTitle>Reports</MuiDialogTitle>
        <MuiDialogContent>
          <ReportProduction contactId={item.id} />
        </MuiDialogContent>
      </MuiDialog>
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild>
          <SidebarMenuItem
            className="min-w-0"
            onMouseEnter={() => onHover(item)}
            onMouseLeave={() => onHover(null)}
            onFocus={() => onFocus(item)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                onFocus(null);
              }
            }}
          >
            <NavLink to={`contacts/${item.id}`} className="block min-w-0 w-full">
              {({ isActive }) => (
                <SidebarMenuButton
                  isActive={isActive}
                  className={cn(
                    "group/contact h-7 min-w-0 w-full justify-start gap-1.5 border border-sidebar-border/50 bg-sidebar-accent/20 px-1.5 text-[11px]"
                  )}
                >
                  <div className="relative w-3.5 h-3.5 shrink-0">
                    <User
                      className={cn(
                        "size-3.5 absolute inset-0 group-hover/contact:opacity-0 transition-opacity"
                      )}
                    />
                    <div
                      className="size-3.5 absolute inset-0 opacity-0 group-hover/contact:opacity-100 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/20 flex justify-center items-center rounded-md transition-all"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(item);
                      }}
                    >
                      <X className="size-3.5 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <span className="min-w-0 flex-1 truncate text-[11px]">{item.name}</span>
                </SidebarMenuButton>
              )}
            </NavLink>
          </SidebarMenuItem>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-64 data-[state=open]:animate-none data-[state=closed]:animate-none">
          <ContextMenuLabel>{item.name}</ContextMenuLabel>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => navigate(`contacts/${item.id}`)}>
            <div className="flex items-center justify-between gap-4">
              <User className="size-5" />
              Go to contact
            </div>
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() =>
              openDialog({ id: item.id, name: item.name } as QueryContact)
            }
          >
            <div className="flex items-center justify-between gap-4">
              <File className="size-5" />
              Doc producer
            </div>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => setReportOpen(true)}>
            <div className="flex items-center justify-between gap-4">
              <ClipboardMinus className="size-5" />
              Report
            </div>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => onDelete(item)}>
            <div className="flex items-center justify-between gap-4">
              <Trash2 className="size-5" />
              Close
            </div>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onDeleteAllOthers(item)}>
            <div className="flex items-center justify-between gap-4">
              <Trash2 className="size-5" />
              Close others
            </div>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onDeleteAll()}>
            <div className="flex items-center justify-between gap-4">
              <Trash2 className="size-5" />
              Close all
            </div>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </>
  );
};
